import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getHealthChecks } from "../lib/api";
import {
  Gauge, Network, Server, Router, Activity, ShieldCheck,
  ArrowRight, ListChecks, Clock, ChevronRight, Cloud, Users,
} from "lucide-react";

const ICONS = {
  gauge: Gauge, network: Network, server: Server, router: Router, activity: Activity,
  cloud: Cloud, users: Users,
};

export default function HealthChecks() {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getHealthChecks().then((d) => { setChecks(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> health-checks <ChevronRight className="h-3 w-3" /> verification
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-6xl">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-400 font-semibold">
          <ShieldCheck className="h-4 w-4" /> Proactive Verification
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Health Checks</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Run these before you leave site (or when everything "seems fine"). Each check confirms a subsystem is
          healthy — with the exact commands, CUCM/RTMT paths, and what <span className="text-emerald-400">healthy output</span> looks like.
        </p>

        {loading ? (
          <div className="mt-8 text-zinc-500 font-mono text-sm">Loading checks…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {checks.map((c) => {
              const Icon = ICONS[c.icon] || Activity;
              return (
                <button
                  key={c.id}
                  data-testid={`healthcheck-card-${c.id}`}
                  onClick={() => navigate(`/health-checks/${c.id}`)}
                  className="group text-left rounded-lg border border-zinc-800 bg-zinc-900 p-5 hover:border-emerald-600/40 hover:-translate-y-px transition-[transform,border-color] duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="h-11 w-11 rounded-md bg-zinc-950 border border-zinc-800 flex items-center justify-center group-hover:border-emerald-500/40">
                      <Icon className="h-5 w-5 text-emerald-400" />
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                      Check
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mt-4">{c.title}</h3>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 font-semibold">{c.category}</p>
                  <p className="text-sm text-zinc-400 mt-2 line-clamp-3">{c.summary}</p>

                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-800 text-[11px] text-zinc-500">
                    <span className="flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" />{c.step_count} checks</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{c.estimated_time}</span>
                    <span className="ml-auto flex items-center gap-1 text-emerald-400 group-hover:gap-2 transition-[gap]">
                      Run <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
